import { Component } from 'react';
import PropTypes from 'prop-types';
import * as styles from './styles/styles.module.scss';
import GiftDisplayItem from '../GiftDisplayItem';
import GiftEditForm from '../GiftEditForm';

import { ServerCommunicationUtil } from '../../../utils/server_communication_util';

const STATUS_TO_WORD_HASH = {
  A: 'Active',
  E: 'Expired',
  R: 'Redeemed',
  U: 'Used'
};

class CouponManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      coupons: [],
      displayMessage: null,
      createNewCouponMode: false,
      importNewCouponMode: false,
      isEditingGift: false
    };
  }

  handleOnInputChange = e => {
    e.preventDefault();
    this.setState({ couponText: e.currentTarget.value });
  };

  handleOnNewCodeInputChange = e => {
    e.preventDefault();
    this.setState({ newCouponText: e.currentTarget.value });
  };

  handleOnNewSourceInputChange = e => {
    e.preventDefault();
    this.setState({ newSource: e.currentTarget.value });
  };

  handleOnNewPriceChange = e => {
    e.preventDefault();
    this.setState({ newPrice: e.currentTarget.value });
  };

  handleOnNewPercentChange = e => {
    e.preventDefault();
    this.setState({ newPercentOff: e.currentTarget.value });
  };

  handleOnNewFreeTrialAmount = e => {
    e.preventDefault();
    this.setState({ newFreeTrialAmount: e.currentTarget.value });
  };

  handleOnNewFreeTrialUnit = e => {
    e.preventDefault();
    this.setState({ newFreeTrialUnit: e.currentTarget.value });
  };

  performCouponSearch = e => {
    this.getCoupons(this.state.couponText);
  };

  clearCouponSearch = e => {
    this.setState({ coupons: [], displayMessage: null, couponText: '' });
  };

  toggleCreateMode = e => {
    this.setState({ createNewCouponMode: !this.state.createNewCouponMode, importNewCouponMode: false, coupons: [], displayMessage: null });
  };

  toggleImportMode = e => {
    this.setState({ importNewCouponMode: !this.state.importNewCouponMode, createNewCouponMode: false, coupons: [], displayMessage: null });
  };

  handleCouponSuccess = response => {
    if (response.length > 0 && response[0].planType) {
      this.setState({
        displayMessage: `Found ${response.length} gift subscription${response.length > 1 ? 's' : ''}`,
        coupons: response
      });
    } else {
      this.setState({
        displayMessage: `Found ${response.length} coupon${response.length > 1 ? 's' : ''}`,
        coupons: response
      });
    }
  };

  handleNewCouponCreateSuccess = response => {
    this.setState({
      coupons: [response[0]],
      displayMessage: 'Coupon created',
      createNewCouponMode: false,
      newPercentOff: null,
      newCouponText: null,
      newPrice: null,
      newFreeTrialAmount: null,
      newFreeTrialUnit: null
    });
  };

  handleNewCouponImportSuccess = response => {
    this.setState({
      coupons: [],
      displayMessage: 'Import processing!',
      importNewCouponMode: false,
      newCouponText: null,
      newSource: null
    });
  };

  handleCouponUpdateSuccess = response => {
    // replace coupon in copy of state array
    const mappedCoupons = this.state.coupons.map((coup, idx) => {
      if (coup.id === response[0].id) {
        return response[0];
      }
      return coup;
    });
    this.setState({ coupons: mappedCoupons, displayMessage: 'Updated coupon' });
  };

  handleGiftUpdateSuccess = response => {
    this.setState({ coupons: response, isEditingGift: false, displayMessage: 'Updated gift subscription' });
  };

  handleCouponFailure = response => {
    if (response && response.status == 404) {
      this.setState({ displayMessage: 'No coupons found' });
    } else {
      console.error("err", response);
      const error_message = response.responseJSON.errors[0].message
      alert(`An error occurred: ${error_message}`);
    }
  };

  getCoupons(couponCode) {
    ServerCommunicationUtil.getApiEndpoint(
      '/api/alltrails/admin/coupons',
      { couponCode },
      this.handleCouponSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  updateCoupon(couponId, params) {
    ServerCommunicationUtil.putApiEndpoint(
      `/api/alltrails/admin/coupons/${couponId}`,
      params,
      this.handleCouponUpdateSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  updateGift(giftId, params) {
    ServerCommunicationUtil.putApiEndpoint(
      `/api/alltrails/gift/${giftId}`,
      params,
      this.handleGiftUpdateSuccess,
      () => alert('An error occurred'),
      () => {}
    );
  }

  saveNewCouponClick = e => {
    e.preventDefault();
    if (
      this.state.newCouponText && 
      (this.state.newPrice && this.state.newPercentOff) ||
      (this.state.newFreeTrialAmount && this.state.newFreeTrialUnit)
    ) {
      this.saveNewCoupon({
        couponCode: this.state.newCouponText,
        price: this.state.newPrice,
        percentOff: this.state.newPercentOff,
        newFreeTrialAmount: this.state.newFreeTrialAmount,
        newFreeTrialUnit: this.state.newFreeTrialUnit
      });
    } else {
      alert('Missing information to create a new coupon.');
    }
  };

  importNewCouponClick = e => {
    e.preventDefault();
    if (this.state.newCouponText) {
      this.importNewCoupon({
        couponCode: this.state.newCouponText,
        source: this.state.newSource
      });
    } else {
      alert('Missing information to create a new coupon.');
    }
  };

  saveNewCoupon(params) {
    ServerCommunicationUtil.postApiEndpoint(
      `/api/alltrails/admin/coupons`,
      params,
      this.handleNewCouponCreateSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  importNewCoupon(params) {
    ServerCommunicationUtil.postApiEndpoint(
      `/api/alltrails/admin/coupon_imports`,
      params,
      this.handleNewCouponImportSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  expireCoupon = couponId => {
    this.updateCoupon(couponId, { status: 'E' });
  };

  deactivateCoupon = couponId => {
    this.updateCoupon(couponId, { status: 'R' });
  };

  renderItemDisplay(item) {
    if (item.planType) {
      return this.renderGiftDiv(item);
    }
    return this.renderCouponDiv(item);
  }

  handleGiftResendSuccess = response => {
    const gift = [...this.state.coupons][0];
    gift.resent = true;
    this.setState({ coupons: [gift] });
  };

  handleResendGiftClick = giftId => {
    ServerCommunicationUtil.postApiEndpoint(
      `/api/alltrails/gift/${giftId}/resend`,
      {},
      this.handleGiftResendSuccess,
      () => alert('An error occurred'),
      () => {}
    );
  };

  handleDeactivateGiftClick = giftId => {
    ServerCommunicationUtil.deleteApiEndpoint(
      `/api/alltrails/gift/${giftId}`,
      {},
      this.clearCouponSearch,
      () => alert('An error occurred'),
      () => {}
    );
  };

  handleSaveGiftClick = newGift => {
    const updateParams = (({ senderEmail, senderName, recipientName, recipientEmail, deliveryDate, message }) => ({
      senderEmail,
      senderName,
      recipientName,
      recipientEmail,
      deliveryDate,
      message
    }))(newGift);
    this.updateGift(newGift.id, updateParams);
  };

  handleEditGiftClick = e => {
    this.setState({ isEditingGift: !this.state.isEditingGift });
  };

  renderGiftDiv(gift) {
    if (this.state.isEditingGift) {
      return <GiftEditForm gift={gift} handleEditGiftClick={this.handleEditGiftClick} handleSaveGiftClick={this.handleSaveGiftClick} />;
    }
    return (
      <GiftDisplayItem
        gift={gift}
        handleEditGiftClick={this.handleEditGiftClick}
        handleResendGiftClick={this.handleResendGiftClick}
        handleDeactivateGiftClick={this.handleDeactivateGiftClick}
      />
    );
  }

  renderCouponDiv(coupon) {
    let expireBtn = null;
    if (coupon.metadata.status === 'A') {
      // Subscription coupons can be used by many users, so we want them to be "expired"
      if (['SubscriptionCoupon', 'FreeTrialCoupon'].includes(coupon.type)) {
        expireBtn = (
          <button className={styles.expireBtn} onClick={() => this.expireCoupon(coupon.id)}>
            Expire
          </button>
        );
        // gift coupons may be bought in error and refunded, in which case we want to set them to be "redeemed"
      } else if (coupon.type === 'GiftCoupon') {
        expireBtn = (
          <button className={styles.expireBtn} onClick={() => this.deactivateCoupon(coupon.id)}>
            Deactivate
          </button>
        );
      }
    }
    let couponInfo = null;
    if (coupon.freeTrialDuration) {
      couponInfo = (
        <h4>{`Free Trial Period: ${coupon.freeTrialDuration}`}</h4>
      );
    } else {
      couponInfo = (
        <div>
          <h4>{`Price: $${coupon.discountedPricePerYear}`}</h4>
          <h4>{`Percent off: ${coupon.discountPercentage}%`}</h4>
        </div>
      );
    }
    return (
      <div className={styles.couponCard}>
        <h4>{`Code: ${coupon.code}`}</h4>
        <h4>{`Type: ${coupon.type}`}</h4>
        {couponInfo}
        <h4>{`Status: ${STATUS_TO_WORD_HASH[coupon.metadata.status]}`}</h4>
        {expireBtn}
      </div>
    );
  }

  renderCouponsContainer() {
    if (!this.createNewCouponMode && this.state.coupons.length > 0) {
      const couponElements = this.state.coupons.map(coup => this.renderItemDisplay(coup));
      return <div className={styles.couponBoxContainer}>{couponElements}</div>;
    }
    return null;
  }

  renderNewCouponForm() {
    if (this.state.createNewCouponMode) {
      return (
        <div>
          <button className={styles.searchButtons} onClick={this.toggleCreateMode}>Cancel</button>
          <button className={styles.searchButtons} onClick={this.saveNewCouponClick}>Save</button>
          <div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewCodeInputChange}
                value={this.state.newCouponText}
                className={styles.searchBox}
                placeholder="Enter new coupon code"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewPriceChange}
                value={this.state.newPrice}
                className={styles.searchBox}
                placeholder="Enter purchase price"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewPercentChange}
                value={this.state.newPercentOff}
                className={styles.searchBox}
                placeholder="Enter percent discount"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewFreeTrialAmount}
                value={this.state.newFreeTrialAmount}
                className={styles.searchBox}
                placeholder="Enter free trial amount"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <select className={styles.searchBox} onChange={this.handleOnNewFreeTrialUnit}>
                <option value="" selected id="defaultOption">Select free trial timeframe</option>
                <option value="day">Day(s)</option>
                <option value="week">Week(s)</option>
                <option value="month">Month(s)</option>
              </select>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  renderRecurlyImportForm() {
    if (this.state.importNewCouponMode) {
      return (
        <div>
          <p>This page is new, and is a way to import both single code and bulk coupons from Recurly into the Monolith. Please take care when importing coupons! If you're uncertain, reach out in #support-growth in Slack.</p>
          <p>Things to note:</p>
          <ul className={styles.listItem}>
            <li>You can monitor the progress of the import in the Slack channel #growth-coupon-imports</li>
            <li>Bulk coupons CANNOT be imported with a source of 'AllTrails'</li>
            <li>The coupon MUST exist in Recurly already</li>
            <li>If the coupon is already active in the Monolith, it will be skipped and considered a success</li>
            <li>Confirm that the import worked appropriately via Looker and/or checking in the Coupon search</li>
          </ul>
          <div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewCodeInputChange}
                value={this.state.newCouponText}
                className={styles.searchBox}
                placeholder="Enter Recurly Coupon Code"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewSourceInputChange}
                value={this.state.newSource}
                className={styles.searchBox}
                placeholder="Enter Coupon Source (Defaults to AllTrails)"
              />
            </div>
          </div>
          <button className={styles.searchButtons} onClick={this.toggleImportMode}>Cancel</button>
          <button className={styles.searchButtons} onClick={this.importNewCouponClick}>Import</button>
        </div>
      );
    }
    return null;
  }

  renderButtonSearchBar() {
    if (this.state.createNewCouponMode || this.state.importNewCouponMode) {
      return null;
    } else {
      return (
        <div>
          <input onChange={this.handleOnInputChange} value={this.state.couponText} className={styles.searchBox} placeholder="Enter coupon code" />
          <button className={styles.searchButtons} onClick={this.performCouponSearch}>
            Find coupons
          </button>
          <button className={styles.searchButtons} onClick={this.clearCouponSearch}>
            Clear search
          </button>
          <button className={styles.searchButtons} onClick={this.toggleCreateMode}>
            Create coupon
          </button>
          <button className={styles.searchButtons} onClick={this.toggleImportMode}>
            Import coupon
          </button>
          <p className={styles.msgDisplay}>{this.state.displayMessage}</p>
        </div>
      );
    }
  }

  renderTitleDiv() {
    let title = null;
    if (this.state.createNewCouponMode) {
      title = 'Creating a coupon';
    } else if (this.state.importNewCouponMode) {
      title = 'Importing a coupon';
    } else {
      title = 'Searching for a coupon';
    }
    return <h2 className={styles.msgDisplay}>{title}</h2>;
  }

  render() {
    return (
      <div className={styles.container}>
        {this.renderTitleDiv()}
        {this.renderButtonSearchBar()}
        {this.renderCouponsContainer()}
        {this.renderNewCouponForm()}
        {this.renderRecurlyImportForm()}
      </div>
    );
  }
}

export default CouponManager;
